import { useEffect, useRef, useState, type DragEvent } from 'react';
import { FilePlus2, GripVertical, X } from 'lucide-react';

export type TimelineDayItem = {
  id: string;
  title: string;
  summary: string | null;
  status: string | null;
  scheduled_for: string | null;
  pub_date: string | null;
  url: string | null;
};

interface TimelineDayModalProps {
  isOpen: boolean;
  dayIso: string;
  items: TimelineDayItem[];
  onClose: () => void;
  onChangeDate: (id: string, scheduledFor: string) => void | Promise<void>;
  onReorder: (orderedIds: string[]) => void | Promise<void>;
  onOpenContent?: (id: string) => void;
  onAddContent?: () => void;
}

function formatDay(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function TimelineDayModal({
  isOpen,
  dayIso,
  items,
  onClose,
  onChangeDate,
  onReorder,
  onOpenContent,
  onAddContent,
}: TimelineDayModalProps) {
  const [local, setLocal] = useState<TimelineDayItem[]>(items);
  const localRef = useRef(local);
  localRef.current = local;
  const [dragId, setDragId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) setLocal(items);
  }, [isOpen, items]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  function onDragStart(id: string, e: DragEvent) {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  }

  function onDragOver(overId: string, e: DragEvent) {
    e.preventDefault();
    if (!dragId || dragId === overId) return;
    setLocal((prev) => {
      const from = prev.findIndex((x) => x.id === dragId);
      const to = prev.findIndex((x) => x.id === overId);
      if (from < 0 || to < 0 || from === to) return prev;
      const next = [...prev];
      const [row] = next.splice(from, 1);
      next.splice(to, 0, row!);
      return next;
    });
  }

  async function onDragEnd() {
    if (!dragId) return;
    setDragId(null);
    await onReorder(localRef.current.map((x) => x.id));
  }

  async function changeDate(id: string, value: string) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
    setSavingId(id);
    try {
      setLocal((prev) =>
        prev.map((x) => (x.id === id ? { ...x, scheduled_for: value } : x)),
      );
      await onChangeDate(id, value);
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-3"
      role="dialog"
      aria-modal="true"
      aria-labelledby="timeline-day-title"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-2">
          <div className="min-w-0">
            <h3
              id="timeline-day-title"
              className="text-sm font-semibold text-gray-900 dark:text-white"
            >
              {formatDay(dayIso)}
            </h3>
            <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5">
              {local.length} content · drag to reorder · edit draft dates
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <ul className="overflow-y-auto px-3 space-y-2 flex-1 min-h-0">
          {local.map((item) => {
            const published =
              (item.status ?? '').toLowerCase() === 'published' && Boolean(item.pub_date);
            const dateValue = published
              ? (item.pub_date ?? '').slice(0, 10)
              : (item.scheduled_for ?? dayIso).slice(0, 10);
            return (
              <li
                key={item.id}
                draggable
                onDragStart={(e) => onDragStart(item.id, e)}
                onDragOver={(e) => onDragOver(item.id, e)}
                onDragEnd={() => void onDragEnd()}
                className={`rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-900/40 px-2.5 py-2 ${
                  dragId === item.id ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    className="mt-0.5 shrink-0 p-0.5 text-gray-400 cursor-grab active:cursor-grabbing"
                    title="Drag to reorder"
                    aria-label="Drag to reorder"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <button
                      type="button"
                      className="w-full text-left text-[13px] font-medium text-gray-900 dark:text-white hover:underline truncate"
                      onClick={() => onOpenContent?.(item.id)}
                    >
                      {item.title}
                    </button>
                    {item.summary?.trim() && (
                      <p className="text-[12px] text-gray-600 dark:text-gray-400 leading-snug line-clamp-3">
                        {item.summary.trim()}
                      </p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono uppercase tracking-wide text-gray-400">
                        {published ? 'published' : 'draft'}
                      </span>
                      <label className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                        Date
                        <input
                          type="date"
                          disabled={published || savingId === item.id}
                          value={dateValue}
                          onChange={(e) => void changeDate(item.id, e.target.value)}
                          className="px-1.5 py-0.5 text-[12px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 disabled:opacity-50"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
          {local.length === 0 && (
            <li className="text-[12px] text-gray-400 dark:text-gray-500 px-1 py-4 text-center">
              No content on this day yet.
            </li>
          )}
        </ul>

        {onAddContent && (
          <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onAddContent}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-md border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40"
            >
              <FilePlus2 className="w-4 h-4" />
              Add content
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
